import React, { Fragment, Suspense, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import Loader from "../components/Loader";
import Toaster from "../components/Toaster/Toaster";
import * as AuthApi from "../api/AuthApi";
import * as SuperAdminPermission from "../page/SUPERADMIN/api/authApi";
import InjectProtectedNode from "./injectProtectedNode";
import UnauthorizerNode from "./unauthorizerNode";
import {
  resetAuthData,
  addAuthData,
} from "../store/slices/authUser/authUserSlice";
import { showUpdatedToasterMessage } from "../store/slices/toaster/toasterslice";
import store from "../store/store";

let isSessionClearing = false;
let refreshPromise = null;
let interceptorsAttached = false;

const isJwtExpired = (token) => {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    if (!payload?.exp) return false;
    return payload.exp * 1000 <= Date.now();
  } catch (e) {
    return true;
  }
};

const clearSessionAndRedirectToLogin = (dispatch, navigate, message) => {
  if (isSessionClearing) return;
  isSessionClearing = true;
  const toastMessage = message || "Invalid or expired refresh token";
  dispatch(resetAuthData());
  localStorage.clear();
  dispatch(
    showUpdatedToasterMessage({
      message: toastMessage,
      type: "Error",
    })
  );
  navigate("/login", { replace: true });
};

const shouldSkipAuthRefresh = (config = {}) => {
  const url = `${config.baseURL || ""}${config.url || ""}`;
  return url.includes("auth/refresh-token") || url.includes("auth/login");
};

const isUnauthorizedResponse = (response) => {
  const bodyUnauthorized =
    response?.data?.error?.code === 401 &&
    (response?.data?.error?.message === "Unauthorized" ||
      response?.data?.error?.message === "Invalid token");

  return bodyUnauthorized;
};

const refreshAccessToken = async (dispatch) => {
  const authSelector = store.getState().ars.authUserReducer;
  const currentRefreshToken = authSelector?.refreshToken;

  if (!currentRefreshToken || isJwtExpired(currentRefreshToken)) {
    throw new Error("Invalid or expired refresh token");
  }

  if (!refreshPromise) {
    refreshPromise = AuthApi.requestToken({ refreshToken: currentRefreshToken })
      .then((res) => {
        if (!res?.accessToken) {
          throw new Error(
            res?.message || "Invalid or expired refresh token"
          );
        }

        const latestAuth = store.getState().ars.authUserReducer;
        dispatch(
          addAuthData({
            ...latestAuth,
            access_token: res.accessToken,
            refreshToken: latestAuth.refreshToken,
          })
        );
        return res.accessToken;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
};

const Layout = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const authSelector = useSelector((state) => state.ars.authUserReducer);

  useEffect(() => {
    const onStorage = (event) => {
      if (event.key === "logout") {
        dispatch({ type: "authUser/resetAuthData", fromStorageEvent: true });
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [dispatch]);

  useEffect(() => {
    if (interceptorsAttached) return undefined;
    interceptorsAttached = true;

    const requestInterceptor = axios.interceptors.request.use(
      (req) => {
        req.baseURL = process.env.REACT_APP_API_URL;
        if (!navigator.onLine) {
          dispatch(
            showUpdatedToasterMessage({
              message: "No internet connection. Please check your network.",
              type: "Error",
            })
          );
          return Promise.reject(new Error("No internet connection"));
        }
        return req;
      },
      (error) => Promise.reject(error)
    );

    const responseInterceptor = axios.interceptors.response.use(
      async (response) => {
        if (!isUnauthorizedResponse(response) || shouldSkipAuthRefresh(response.config)) {
          return response;
        }

        if (response.config.__isRetryRequest) {
          clearSessionAndRedirectToLogin(
            dispatch,
            navigate,
            "Invalid or expired refresh token"
          );
          return response;
        }

        try {
          const accessToken = await refreshAccessToken(dispatch);
          const originalRequest = {
            ...response.config,
            __isRetryRequest: true,
            headers: {
              ...response.config.headers,
              Authorization: `Bearer ${accessToken}`,
            },
          };
          return axios(originalRequest);
        } catch (errors) {
          clearSessionAndRedirectToLogin(
            dispatch,
            navigate,
            errors?.message || "Invalid or expired refresh token"
          );
          return response;
        }
      },
      async (error) => {
        if (error.code === "ERR_NETWORK") {
          dispatch(
            showUpdatedToasterMessage({
              message: error?.message,
              type: "Error",
            })
          );
          return Promise.reject(error);
        }

        const status = error?.response?.status;
        const originalRequest = error?.config;

        if (
          (status === 401 || status === 403) &&
          originalRequest &&
          !shouldSkipAuthRefresh(originalRequest) &&
          !originalRequest.__isRetryRequest
        ) {
          try {
            const accessToken = await refreshAccessToken(dispatch);
            originalRequest.__isRetryRequest = true;
            originalRequest.headers = {
              ...originalRequest.headers,
              Authorization: `Bearer ${accessToken}`,
            };
            return axios(originalRequest);
          } catch (errors) {
            clearSessionAndRedirectToLogin(
              dispatch,
              navigate,
              errors?.message ||
              error?.response?.data?.message ||
              "Invalid or expired refresh token"
            );
            return Promise.reject(error);
          }
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
      interceptorsAttached = false;
    };
  }, [dispatch, navigate]);

  useEffect(() => {
    if (!authSelector?.auth) return;

    const accessExpired = isJwtExpired(authSelector.access_token);
    const refreshExpired = isJwtExpired(authSelector.refreshToken);

    if (accessExpired && refreshExpired) {
      clearSessionAndRedirectToLogin(
        dispatch,
        navigate,
        "Invalid or expired refresh token"
      );
      return;
    }

    if (accessExpired && !refreshExpired) {
      refreshAccessToken(dispatch).catch((errors) => {
        clearSessionAndRedirectToLogin(
          dispatch,
          navigate,
          errors?.message || "Invalid or expired refresh token"
        );
      });
    }
  }, [authSelector?.auth, authSelector?.access_token, authSelector?.refreshToken, dispatch, navigate]);

  const updateuserPermission = async () => {
    try {
      const res = await AuthApi.updatePermission();
      if (res?.status == true) {
        const newData = {
          ...authSelector,
          user_permissions: [...res.data.user_permissions],
        };
        dispatch(addAuthData(newData));
      }
    } catch (e) { }
  };

  const updateSuperAdminPermission = async () => {
    try {
      const res = await SuperAdminPermission.updatePermissionSa();
      if (res?.status == true) {
        const newData = {
          ...authSelector,
          user_permissions: [...res.data.user_permissions],
        };
        dispatch(addAuthData(newData));
      }
    } catch (e) { }
  };

  useEffect(() => {
    if (authSelector?.access_token) {
      if (authSelector?.user?.user_type === "AA") {
        updateuserPermission();
      } else if (authSelector?.user?.user_type === "SU") {
        updateSuperAdminPermission();
      }
    }
  }, [location]);

  return (
    <Fragment>
      {authSelector.auth ? (
        <InjectProtectedNode authSelector={authSelector} />
      ) : (
        <Suspense fallback={<Loader />}>
          <UnauthorizerNode />
        </Suspense>
      )}
      <Toaster />
    </Fragment>
  );
};

export default Layout;
